// Home v2 — Gas Tank entry point (mobile-web). The extension dashboard surfaces
// this as GasTankButton in DashboardHeader; the Home v2 shell has no header, so
// the pill lives on the balance card instead. It sits on the mode gradient, so
// the palette here is the card's translucent-white scale, like the other
// on-gradient pills, not the theme surface colors.
import React, { useCallback, useMemo } from 'react'
import { Pressable, View } from 'react-native'
import { useNavigate } from 'react-router-dom'

import GasTankIcon from '@common/assets/svg/GasTankIcon'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { getGasTankTokenDetails } from '@common/utils/getGasTankTokenDetails'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

const GasTankPill: React.FC = () => {
  const navigate = useNavigate()
  const { account, portfolio } = useSelectedAccountControllerState()
  const { networks } = useNetworksControllerState()

  const gasTank = useMemo(
    () => getGasTankTokenDetails(portfolio, account, networks),
    [account, networks, portfolio]
  )

  // Absolute — WEB_ROUTES values carry no leading slash, and a relative navigate
  // from /dashboard resolves to /dashboard/top-up-gas-tank, which matches nothing.
  const goToTopUp = useCallback(() => navigate(`/${WEB_ROUTES.topUpGasTank}`), [navigate])

  if (!portfolio?.isReadyToVisualize) {
    return <SkeletonLoader lowOpacity width={160} height={28} borderRadius={8} />
  }

  // No gas-tank portfolio slice at all (a failed fetch) — say nothing rather
  // than offer a top-up that can't be estimated. An EMPTY tank is not that: a
  // freshly created account legitimately has no rows and still has to be able
  // to top up (Trello #478).
  if (gasTank.isUnavailable) return null

  const hasBalance = !!gasTank.balanceFormatted

  const content = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-start',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
        backgroundColor: 'rgba(255,255,255,0.16)'
      }}
    >
      <GasTankIcon width={18} height={18} color="#fff" />
      {hasBalance ? (
        <>
          <Text fontSize={12} weight="number_medium" color="#fff">
            {gasTank.balanceUSDFormatted}
          </Text>
          <Text fontSize={12} color="rgba(255,255,255,0.75)">
            on Gas Tank
          </Text>
        </>
      ) : (
        <Text fontSize={12} weight="number_bold" color="#fff">
          Top up Gas Tank
        </Text>
      )}
    </View>
  )

  // Always pressable — ineligible accounts (hardware-key EOA, view-only) land on
  // the top-up screen, which explains why the gas tank is unavailable for them.
  return (
    <Pressable onPress={goToTopUp} hitSlop={6} testID="dashboard-gas-tank-button">
      {content}
    </Pressable>
  )
}

export default React.memo(GasTankPill)
